import api from './index'
import type { ApiResponse, V1ApiResponse } from '@/types'
import { ElMessage } from 'element-plus'
import { Loading } from '@element-plus/icons-vue'

/**
 * 文件上传配置
 */
interface UploadConfig {
  showLoading?: boolean // 是否显示加载提示
  loadingText?: string // 加载提示文本
}

/**
 * 上传响应数据
 */
interface UploadResponse {
  name: string // 文件名
  contentType: string // 文件类型
  size: number // 文件大小（字节）
  url: string // 文件访问URL
  path: string // 服务器存储路径
  createTime: string // 创建时间
  source: string | null // 来源
}

/** POST /api/v1/users/me/media-resources/upload 及列表项 data */
export interface UserMediaResource {
  mediaResourceId: number
  mediaUrl: string
  ossRegionId: string
  ossBucketName: string
  ossObjectName: string
  mediaType: number
  fileSizeKb: number
  durationSeconds: number
  coverUrl?: string | null
  videoFrameImages?: string[]
  contentType: string
  compressed: boolean
  createDateTime: string
  metadata?: Record<string, string>
}

export type ListUserMediaResourcesParams = {
  mediaType?: number
  startTime?: string
  endTime?: string
  page?: number
  pageSize?: number
}

export type UserMediaResourcePage = {
  list: UserMediaResource[]
  total: number
  page: number
  pageSize: number
}

export type DeleteUserMediaResourcesPayload = {
  mediaResourceIds: number[]
}

export type DeleteUserMediaResourcesResponse = {
  deletedCount?: number
  deletedAt?: string
}

/**
 * 文件上传 API
 */
export const uploadApi = {
  /**
   * 上传图片
   * @param file 文件对象
   * @param config 上传配置
   * @returns Promise<上传结果>
   */
  uploadImage: async (
    file: File,
    config?: UploadConfig
  ): Promise<{ success: boolean; url?: string; message?: string }> => {
    const {
      // maxSize = 10 * 1024 * 1024, // 默认 10MB
      // allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'],
      showLoading = true,
      loadingText = '图片上传中...',
    } = config || {}

    let loadingMsg: any = null

    try {
      // 显示加载提示
      if (showLoading) {
        loadingMsg = ElMessage({
          message: loadingText,
          duration: 0,
          showClose: false,
          customClass: 'upload-loading-message',
          icon: h(Loading, { class: 'is-loading' }),
        })
      }

      // 构建表单数据
      const formData = new FormData()
      formData.append('file', file)

      // 调用上传接口
      const response: ApiResponse<UploadResponse> = await api.post(
        '/api-file/files/upload',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      )

      // 关闭加载提示
      if (loadingMsg) {
        loadingMsg.close()
      }

      if (response.resp_code === 0 && response.datas) {
        // 上传成功，获取图片URL
        if (showLoading) {
          ElMessage.success('图片上传成功')
        }
        return { success: true, url: response.datas.url }
      } else {
        ElMessage.error(response.resp_msg || '图片上传失败')
        return { success: false, message: response.resp_msg || '上传失败' }
      }
    } catch (error) {
      // 关闭加载提示
      if (loadingMsg) {
        loadingMsg.close()
      }

      console.error('图片上传失败:', error)
      ElMessage.error('网络开小差了，请稍后重试')
      return { success: false, message: '网络错误' }
    }
  },

  /**
   * 上传多张图片
   * @param files 文件数组
   * @param config 上传配置
   * @returns Promise<上传结果数组>
   */
  uploadImages: async (
    files: File[],
    config?: UploadConfig
  ): Promise<Array<{ success: boolean; url?: string; message?: string }>> => {
    const uploadPromises = files.map((file) => uploadApi.uploadImage(file, config))
    return Promise.all(uploadPromises)
  },

  /**
   * 上传视频
   * @param file 文件对象
   * @param config 上传配置
   * @returns Promise<上传结果>
   */
  uploadVideo: async (
    file: File,
    config?: UploadConfig
  ): Promise<{ success: boolean; url?: string; message?: string }> => {
    const { showLoading = true, loadingText = '视频上传中...' } = config || {}

    let loadingMsg: any = null

    try {
      // 显示加载提示
      if (showLoading) {
        loadingMsg = ElMessage({
          message: loadingText,
          duration: 0,
          showClose: false,
          customClass: 'upload-loading-message',
          icon: h(Loading, { class: 'is-loading' }),
        })
      }

      // 构建表单数据
      const formData = new FormData()
      formData.append('file', file)

      // 调用上传接口
      const response: ApiResponse<UploadResponse> = await api.post(
        '/api-file/files/upload',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      )

      // 关闭加载提示
      if (loadingMsg) {
        loadingMsg.close()
      }

      if (response.resp_code === 0 && response.datas) {
        if (showLoading) {
          ElMessage.success('视频上传成功')
        }
        return { success: true, url: response.datas.url }
      } else {
        ElMessage.error(response.resp_msg || '视频上传失败')
        return { success: false, message: response.resp_msg || '上传失败' }
      }
    } catch (error) {
      // 关闭加载提示
      if (loadingMsg) {
        loadingMsg.close()
      }

      console.error('视频上传失败:', error)
      ElMessage.error('网络开小差了，请稍后重试')
      return { success: false, message: '网络错误' }
    }
  },

  /**
   * 通用文件上传
   * @param file 文件对象
   * @param config 上传配置
   * @returns Promise<上传结果>
   */
  uploadFile: async (
    file: File,
    config?: UploadConfig
  ): Promise<{ success: boolean; url?: string; message?: string }> => {
    const { showLoading = true, loadingText = '文件上传中...' } = config || {}

    let loadingMsg: any = null

    try {
      // 显示加载提示
      if (showLoading) {
        loadingMsg = ElMessage({
          message: loadingText,
          duration: 0,
          showClose: false,
          customClass: 'upload-loading-message',
          icon: h(Loading, { class: 'is-loading' }),
        })
      }

      // 构建表单数据
      const formData = new FormData()
      formData.append('file', file)

      // 调用上传接口
      const response: ApiResponse<UploadResponse> = await api.post(
        '/api-file/files/upload',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      )

      // 关闭加载提示
      if (loadingMsg) {
        loadingMsg.close()
      }

      if (response.resp_code === 0 && response.datas) {
        if (showLoading) {
          ElMessage.success('文件上传成功')
        }
        return { success: true, url: response.datas.url }
      } else {
        ElMessage.error(response.resp_msg || '文件上传失败')
        return { success: false, message: response.resp_msg || '上传失败' }
      }
    } catch (error) {
      // 关闭加载提示
      if (loadingMsg) {
        loadingMsg.close()
      }

      console.error('文件上传失败:', error)
      ElMessage.error('网络开小差了，请稍后重试')
      return { success: false, message: '网络错误' }
    }
  },

  /**
   * 当前登录用户媒体资源分页查询
   * GET /api/v1/users/me/media-resources
   */
  listMediaResources: (
    params?: ListUserMediaResourcesParams
  ): Promise<V1ApiResponse<UserMediaResourcePage>> =>
    api.get('/v1/users/me/media-resources', { params }),

  /**
   * 当前登录用户媒体资源批量删除
   * POST /api/v1/users/me/media-resources/delete
   */
  deleteMediaResources: (
    payload: DeleteUserMediaResourcesPayload
  ): Promise<V1ApiResponse<DeleteUserMediaResourcesResponse>> =>
    api.post('/v1/users/me/media-resources/delete', payload),

  /**
   * 当前登录用户媒体资源上传
   * POST /api/v1/users/me/media-resources/upload
   */
  uploadMediaResource: async (
    file: File,
    config?: UploadConfig & { remark?: string },
  ): Promise<{ success: boolean; data?: UserMediaResource; url?: string; message?: string }> => {
    const { showLoading = false, loadingText = '上传中…', remark } = config || {}
    let loadingMsg: any = null

    try {
      if (showLoading) {
        loadingMsg = ElMessage({
          message: loadingText,
          duration: 0,
          showClose: false,
          customClass: 'upload-loading-message',
          icon: h(Loading, { class: 'is-loading' }),
        })
      }

      const formData = new FormData()
      formData.append('file', file)
      if (remark) formData.append('remark', remark)

      const response: V1ApiResponse<UserMediaResource> = await api.post(
        '/v1/users/me/media-resources/upload',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          timeout: 180000,
        },
      )

      if (loadingMsg) loadingMsg.close()

      if (response.code === 0 && response.data?.mediaUrl) {
        return { success: true, data: response.data, url: response.data.mediaUrl }
      }
      ElMessage.error(response.message || '媒体上传失败')
      return { success: false, message: response.message || '上传失败' }
    } catch (error) {
      if (loadingMsg) loadingMsg.close()
      console.error('媒体资源上传失败:', error)
      ElMessage.error('网络开小差了，请稍后重试')
      return { success: false, message: '网络错误' }
    }
  },
}

export default uploadApi
